import { useCallback, useMemo } from "react";
import type {
    TApiColumnMappingFieldType,
    TApiColumnMappingFileType,
    TApiClient,
} from "@/apis/proda_finance";

type TColumnMappingState = {
    data: Array<{
        id: string;
        fileType: TApiColumnMappingFileType;
        fieldType: TApiColumnMappingFieldType;
        columnName: string;
        headerRowNumber: number;
        isActive: boolean;
        availableColumns: string[] | null;
    }> | null;
    loading: boolean;
};

export const useDealDetailMappingActions = (
    api: TApiClient,
    setTbColumnMappings: React.Dispatch<React.SetStateAction<TColumnMappingState>>,
    setGlColumnMappings: React.Dispatch<React.SetStateAction<TColumnMappingState>>,
) => {
    const onCreatePropcodeMapping = useCallback(
        async (
            dealId: string,
            data: {
                source_property_code: string;
                target_property_code: string;
                kind: string;
            },
        ) => {
            await api.deals.createPropcodeMapping(dealId, data);
        },
        [],
    );

    const onUpdatePropcodeMapping = useCallback(
        async (
            dealId: string,
            mappingId: string,
            data: {
                kind?: string;
                source_property_code?: string;
                target_property_code?: string;
                is_active?: boolean;
            },
        ) => {
            await api.deals.updatePropcodeMapping(dealId, mappingId, data);
        },
        [],
    );

    const onDeletePropcodeMapping = useCallback(async (dealId: string, mappingId: string) => {
        await api.deals.deletePropcodeMapping(dealId, mappingId);
    }, []);

    const columnMappingActions = useMemo(
        () => ({
            fetchMappings: async (dealId: string, fileType: "tb" | "gl") => {
                const setter = fileType === "tb" ? setTbColumnMappings : setGlColumnMappings;
                setter({ data: null, loading: true });
                try {
                    const mappingsRes = await api.deals.getColumnMappings(dealId, {
                        file_type: fileType,
                    });
                    setter({
                        data: mappingsRes.items.map((m) => ({
                            id: m.id,
                            fileType: m.file_type,
                            fieldType: m.field_type,
                            columnName: m.column_name,
                            headerRowNumber: m.header_row_number,
                            isActive: m.is_active,
                            availableColumns: m.available_columns,
                        })),
                        loading: false,
                    });
                } catch {
                    setter({ data: [], loading: false });
                }
            },
            updateMappings: async (
                dealId: string,
                fileType: "tb" | "gl",
                headerRowNumber: number,
                mappings: Array<{
                    field_type: TApiColumnMappingFieldType;
                    column_name: string;
                }>,
                availableColumns?: string[],
            ) => {
                await api.deals.updateColumnMappings(dealId, {
                    file_type: fileType,
                    header_row_number: headerRowNumber,
                    mappings,
                    available_columns: availableColumns,
                });
            },
            extractColumns: async (file: File, headerRowNumber: number) => {
                return await api.deals.extractColumns(file, headerRowNumber);
            },
        }),
        [],
    );

    return {
        onCreatePropcodeMapping,
        onUpdatePropcodeMapping,
        onDeletePropcodeMapping,
        columnMappingActions,
    };
};
